import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';
import { RefreshCw, Download, Calendar, Zap, Thermometer, Activity, TrendingUp, AlertTriangle, Droplets, Settings } from 'lucide-react';
import InverterEfficiencyHeatmap from '../charts/InverterEfficiencyHeatmap';
import InverterClippingChart from '../charts/InverterClippingChart';
import InverterBreakdownChart from '../charts/InverterBreakdownChart';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import DatePickerButton from '../ui/date-picker-button';
import { useToast } from '../../hooks/use-toast';
import apiService from '../../services/api';
import { API_CONFIG } from '../../config/api';
import moment from 'moment';
import { todayISO } from '../../lib/utils';

const InverterAsset = () => {
  const [inverters, setInverters] = useState([]);
  const [selectedInverters, setSelectedInverters] = useState([]);
  const [selectedParameter, setSelectedParameter] = useState('performance_ratio');
  const [chartType, setChartType] = useState('line');
  const [isLoading, setIsLoading] = useState(false);
  const [chartData, setChartData] = useState([]);
  const [kpiData, setKpiData] = useState([]);
  const [efficiencyData, setEfficiencyData] = useState({});
  
  // Date states
  const yesterdayStr = moment().subtract(1, 'day').format('YYYY-MM-DD');
  const [startDate, setStartDate] = useState(yesterdayStr);
  const [endDate, setEndDate] = useState(yesterdayStr);
  
  const { toast } = useToast();

  // Parameter options grouped by category
  const parameterOptions = [
    { 
      category: 'Observed Parameters', 
      options: [
        { value: 'active_power', label: 'Active Power (kW)' },
        { value: 'rms_voltage', label: 'Inverter RMS Phase (V)' },
        { value: 'heatsink_temp', label: 'Heatsink Temperature Average (°C)' },
        { value: 'output_current', label: 'Inverter Total Output Current (A)' }
      ]
    },
    { 
      category: 'Live KPI', 
      options: [
        { value: 'specific_power', label: 'Specific Power (kW/kWp)' },
        { value: 'efficiency', label: 'Efficiency (%)' },
        { value: 'availability_live', label: 'Availability Live' }
      ]
    },
    { 
      category: 'Cumulative KPI', 
      options: [
        { value: 'specific_yield', label: 'Specific Yield (kW/kWp)' },
        { value: 'availability_daily', label: 'Availability Daily (%)' },
        { value: 'performance_ratio', label: 'Performance Ratio (%)' },
        { value: 'energy_export', label: 'Energy Export (MWh)' },
        { value: 'energy_export_lifetime', label: 'Energy Export Lifetime (MWh)' }
      ]
    }
  ];

  useEffect(() => {
    fetchInverterList();
  }, []);

  useEffect(() => {
    if (selectedInverters.length > 0) {
      fetchInverterData();
    }
  }, [selectedInverters, selectedParameter, startDate, endDate]);

  const fetchInverterList = async () => {
    try {
      setIsLoading(true);

      // Determine date range (today by default)
      const start = moment(startDate).format('YYYY-MM-DD');
      const end = moment(endDate).format('YYYY-MM-DD');

      const res = await apiService.getForEnergyLoss({
        start_date: `${start} 00:00:00`,
        end_date: `${end} 23:55:00`,
        plant_id: API_CONFIG.PLANT_ID,
        asset_id_list: [API_CONFIG.PLANT_ID]
      });

      const arr = Array.isArray(res.data) ? res.data : (res.data?.body || []);
      let processedInverters = [];

      if (Array.isArray(arr) && arr.length > 0) {
        const eya = arr[0]?.readings?.[0]?.EYA || {};
        const inverterIds = Object.keys(eya.inverter_clipping_loss_list_percentage || {});
        processedInverters = inverterIds.map(id => ({
          asset_id: id,
          display_name: id,
          type: 'INV',
          make: '-',
          model: '-',
          capacity: '-',
        }));
      }

      setInverters(processedInverters);
      setSelectedInverters(processedInverters.slice(0, 5).map(inv => inv.asset_id));
    } catch (error) {
      console.error('Error fetching inverters:', error);
      // Quietly fail without user-facing toast
    } finally {
      setIsLoading(false);
    }
  };

  const fetchInverterData = async () => {
    try {
      setIsLoading(true);
      
      const formattedStartDate = moment(startDate).format('YYYY-MM-DD') + ' 00:00:00';
      const formattedEndDate = moment(endDate).format('YYYY-MM-DD') + ' 23:59:59';
      
      // Fetch KPI data for selected inverters
      const kpiResponse = await apiService.getKpiReadingsByTags({
        start_date: formattedStartDate,
        end_date: formattedEndDate,
        plant_id: API_CONFIG.PLANT_ID,
        asset_id_list: selectedInverters,
        tags_list: [selectedParameter]
      });

      if (kpiResponse.data && kpiResponse.data.json_object) {
        processChartData(kpiResponse.data.json_object);
        processKpiData(kpiResponse.data.json_object);
      }
      
      // Fetch efficiency data
      fetchEfficiencyData();
      
    } catch (error) {
      console.error('Error fetching inverter data:', error);
      // Quietly fail without user-facing toast
    } finally {
      setIsLoading(false);
    }
  };

  const fetchEfficiencyData = async () => {
    try {
      const start = moment(startDate).format('YYYY-MM-DD');
      const end = moment(endDate).format('YYYY-MM-DD');
      const res = await apiService.getForEnergyLoss({
        start_date: `${start} 00:00:00`,
        end_date: `${end} 23:55:00`,
        plant_id: API_CONFIG.PLANT_ID,
        asset_id_list: [API_CONFIG.PLANT_ID]
      });
      const arr = Array.isArray(res.data) ? res.data : (res.data?.body || []);
      if (!Array.isArray(arr) || arr.length === 0) return;
      const eya = arr[0]?.readings?.[0]?.EYA || {};

      const efficiencyMetrics = {
        soiling_loss: Math.abs(Number(eya.soiling_loss ?? 0)),
        efficiency: 100 + Number(eya.inverter_efficiency_loss ?? 0),
        breakdown_hours: Number(eya.plant_breakdown_loss_kwh ?? 0),
        clipping_loss: Math.abs(Number(eya.plant_clipping_loss_percentage ?? 0)),
        deration_loss: Math.abs(Number(eya.plant_deration_loss_percentage ?? 0)),
        availability: Number(eya.plant_availability?.plant_availability ?? 0)
      };
      setEfficiencyData(efficiencyMetrics);
    } catch (error) {
      console.error('Error fetching efficiency data:', error);
    }
  };

  const processChartData = (jsonData) => {
    const data = [];
    const timePoints = new Set();
    
    // Extract all unique time points
    selectedInverters.forEach(inverterId => {
      if (jsonData[inverterId]) {
        Object.keys(jsonData[inverterId]).forEach(time => timePoints.add(time));
      }
    });
    
    // Sort time points
    const sortedTimes = Array.from(timePoints).sort();
    
    // Build chart data
    sortedTimes.forEach(time => {
      const dataPoint = { time: moment(time).format('HH:mm') };
      
      selectedInverters.forEach(inverterId => {
        const inverter = inverters.find(inv => inv.asset_id === inverterId);
        const displayName = inverter?.display_name || inverterId;
        
        if (jsonData[inverterId] && jsonData[inverterId][time]) {
          dataPoint[displayName] = parseFloat(jsonData[inverterId][time].value || 0);
        } else {
          dataPoint[displayName] = 0;
        }
      });
      
      data.push(dataPoint);
    });
    
    setChartData(data);
  };

  const processKpiData = (jsonData) => {
    const kpis = [];
    
    selectedInverters.forEach(inverterId => {
      const inverter = inverters.find(inv => inv.asset_id === inverterId);
      const latestData = jsonData[inverterId] ? Object.values(jsonData[inverterId]).pop() : null;
      
      kpis.push({
        inverter: inverter?.display_name || inverterId,
        value: latestData?.value || 0,
        unit: latestData?.unit || '',
        status: latestData?.value > 90 ? 'good' : latestData?.value > 80 ? 'warning' : 'critical'
      });
    });
    
    setKpiData(kpis);
  };

  const handleInverterToggle = (inverterId) => {
    setSelectedInverters(prev => {
      if (prev.includes(inverterId)) {
        return prev.filter(id => id !== inverterId);
      } else {
        return [...prev, inverterId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedInverters.length === inverters.length) {
      setSelectedInverters([]);
    } else {
      setSelectedInverters(inverters.map(inv => inv.asset_id));
    }
  };

  const exportData = () => {
    const headers = ['Time', ...selectedInverters.map(id => {
      const inv = inverters.find(i => i.asset_id === id);
      return inv?.display_name || id;
    })];
    
    const csvRows = [headers.join(',')];
    
    chartData.forEach(row => {
      const values = [row.time];
      selectedInverters.forEach(id => {
        const inv = inverters.find(i => i.asset_id === id);
        const displayName = inv?.display_name || id;
        values.push(row[displayName] || 0);
      });
      csvRows.push(values.join(','));
    });
    
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `inverter-data-${moment().format('YYYY-MM-DD')}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    
    toast({
      title: "Success",
      description: "Data exported successfully"
    });
  };

  const Chart = chartType === 'line' ? LineChart : BarChart;
  const DataComponent = chartType === 'line' ? Line : Bar;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Inverter Management
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Monitor and analyze inverter performance
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <Button variant="outline" onClick={exportData}>
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
          <Button onClick={fetchInverterData} disabled={isLoading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Inverter Performance Charts */}
      <div className="grid grid-cols-1 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Inverter Efficiency (%)</CardTitle>
          </CardHeader>
          <CardContent className="h-[500px]">
            <InverterEfficiencyHeatmap startDate={startDate} endDate={endDate} />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Inverter Clipping Loss (%)</CardTitle>
          </CardHeader>
          <CardContent className="h-[500px]">
            <InverterClippingChart startDate={startDate} endDate={endDate} />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Inverter Breakdown (kWh)</CardTitle>
          </CardHeader>
          <CardContent className="h-[500px]">
            <InverterBreakdownChart startDate={startDate} endDate={endDate} />
          </CardContent>
        </Card>
      </div>

      {/* KPI Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {kpiData.map((kpi, index) => (
          <Card key={index}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{kpi.inverter}</p>
                  <p className="text-2xl font-bold">
                    {kpi.value.toFixed(2)} {kpi.unit}
                  </p>
                </div>
                <div className={`p-2 rounded-lg ${
                  kpi.status === 'good' ? 'bg-green-100 dark:bg-green-900/20' :
                  kpi.status === 'warning' ? 'bg-yellow-100 dark:bg-yellow-900/20' :
                  'bg-red-100 dark:bg-red-900/20'
                }`}>
                  <Activity className={`h-5 w-5 ${
                    kpi.status === 'good' ? 'text-green-600 dark:text-green-400' :
                    kpi.status === 'warning' ? 'text-yellow-600 dark:text-yellow-400' :
                    'text-red-600 dark:text-red-400'
                  }`} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Efficiency Metrics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <TrendingUp className="h-5 w-5" />
              <span>Efficiency Metrics</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600 dark:text-gray-400">Soiling Loss</span>
                <span className="font-medium">{efficiencyData.soiling_loss?.toFixed(2) || '-'}%</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600 dark:text-gray-400">Efficiency</span>
                <span className="font-medium text-green-600">{efficiencyData.efficiency?.toFixed(2) || '-'}%</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600 dark:text-gray-400">Breakdown Hours</span>
                <span className="font-medium text-red-600">{typeof efficiencyData.breakdown_hours === 'number' ? efficiencyData.breakdown_hours.toFixed(2) : '-'} hrs</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600 dark:text-gray-400">Clipping Loss</span>
                <span className="font-medium">{efficiencyData.clipping_loss?.toFixed(2) || '-'}%</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600 dark:text-gray-400">Deration Loss</span>
                <span className="font-medium">{efficiencyData.deration_loss?.toFixed(2) || '-'}%</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600 dark:text-gray-400">Availability</span>
                <span className="font-medium text-blue-600">{efficiencyData.availability?.toFixed(2) || '-'}%</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <AlertTriangle className="h-5 w-5" />
              <span>Recent Alerts</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-start space-x-3">
                <div className="p-2 bg-yellow-100 dark:bg-yellow-900/20 rounded">
                  <Thermometer className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium">High Temperature Warning</p>
                  <p className="text-xs text-gray-500">INV-003 - 2 hours ago</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="p-2 bg-red-100 dark:bg-red-900/20 rounded">
                  <Zap className="h-4 w-4 text-red-600 dark:text-red-400" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium">DC Input Voltage Fluctuation</p>
                  <p className="text-xs text-gray-500">INV-007 - 5 hours ago</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="p-2 bg-orange-100 dark:bg-orange-900/20 rounded">
                  <Activity className="h-4 w-4 text-orange-600 dark:text-orange-400" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium">Performance Below Threshold</p>
                  <p className="text-xs text-gray-500">INV-012 - 1 day ago</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default InverterAsset;
